import ScopeChangeRequestStore from 'admin/ScopeChangeRequestStore'
import ScopeChangeRequest, {
  ScopeChangeRequestItem,
} from 'admin/scope-change-requests/ScopeChangeRequest'
import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import { makeObservable, action, observable } from 'mobx'
import { task } from 'mobx-task'
import { validateElements } from 'utils/validation-utils'
import { validationContext } from 'validx'
import { v4 } from 'uuid'
import AddInventoryItemsToEditDialogViewModel, {
  EditScopeChangeInventoryItemToAdd,
} from '../AddInventoryItemsToEditDialog/AddInventoryItemsToEditDialogViewModel'
import InventoryStore from 'admin/InventoryStore'

export default class ScopeChangeEditInventoryItemsDialogViewModel extends ModalViewModel {
  @observable
  scopeChangeRequest = ScopeChangeRequest.none

  @observable
  editScopeChangeInventoryItems: EditScopeChangeInventoryItemViewModel[] = []

  readonly scopeChangesValidationStep = validationContext(this, {
    editScopeChangeInventoryItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  addInventoryItemsToEditDialogVM = new AddInventoryItemsToEditDialogViewModel(
    this.inventoryStore,
    this.handleItemsAdded,
  )

  constructor(
    private inventoryStore: InventoryStore,
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private onSave: () => void,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Adds items to the inventory items to edit
   */
  @action.bound
  handleItemsAdded(itemsToAdd: EditScopeChangeInventoryItemToAdd[]) {
    const editItems = itemsToAdd
      .filter(
        (item) =>
          !this.editScopeChangeInventoryItems
            .map((i) => i.versionId)
            .includes(item.versionId),
      )
      .map((item) => {
        const previousItem = this.scopeChangeRequest.job.jobScopeItems.find(
          (c) => c.inventory_item_id === item.itemId,
        )

        const versionId =
          previousItem?.inventory_item_version_id ?? item.versionId
        const name = previousItem?.name ?? item.name
        const currentQuantity = previousItem?.quantity ?? 0
        // Let's start the quantity editor at a previous quantity if we have
        const newQuantity = previousItem?.quantity ?? 1

        return new EditScopeChangeInventoryItemViewModel(
          versionId,
          name,
          newQuantity,
          currentQuantity,
        )
      })
    this.editScopeChangeInventoryItems =
      this.editScopeChangeInventoryItems.concat(editItems)
  }

  /**
   * Opens the dialog
   * @param scopeChangeRequest
   */
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.editScopeChangeInventoryItems = scopeChangeRequest.inventoryItems.map(
      EditScopeChangeInventoryItemViewModel.fromInventoryScopeChangeItem,
    )
    this.show()
  }

  /**
   * Edit the scope change request and close the dialog
   */
  save = task.resolved(async () => {
    if (!this.scopeChangesValidationStep.validate().isValid) {
      return
    }

    await this.scopeChangeRequestStore
      .editScopeChangeRequest(
        this.scopeChangeRequest.id,
        this.scopeChangeRequest.customItems.map((i) => ({
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          proPrice: i.proPrice,
        })),
        this.editScopeChangeInventoryItems.map((i) => ({
          versionId: i.versionId,
          quantity: i.newQuantity,
        })),
      )
      .then(async () => {
        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Scope change items updated')
          .autoDismiss(2000)
          .show()

        await this.scopeChangeRequestStore.fetchScopeChangeRequest(
          this.scopeChangeRequest.id,
        )

        this.onSave()
        this.close()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', e.message)
          .autoDismiss(2000)
          .show()
      })
  })

  /**
   * Removes a previously added custom scope change.
   *
   * @param key
   */
  @action.bound
  removeScopeChange(versionId: string) {
    const pos = this.editScopeChangeInventoryItems.findIndex(
      (item) => item.versionId === versionId,
    )
    this.editScopeChangeInventoryItems.splice(pos, 1)
  }
}

export class EditScopeChangeInventoryItemViewModel {
  @observable key = ''

  @observable versionId = ''

  @observable name = ''

  /**
   * The new quantity of the item
   */
  @observable newQuantity = 1

  /**
   * The current quantity of the item
   */
  @observable currentQuantity = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) => opts.value >= 0 || 'Quantity cannot be less than zero.',
    ],
  })

  constructor(
    versionId: string,
    name: string,
    newQuantity: number,
    currentQuantity: number,
  ) {
    this.key = v4()
    this.versionId = versionId
    this.name = name
    this.newQuantity = newQuantity
    this.currentQuantity = currentQuantity
    makeObservable(this)
  }

  @action.bound
  incrementQuantity() {
    this.newQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.newQuantity === 0) {
      return
    }

    this.newQuantity -= 1
  }

  @action.bound
  setQuantity(value: string) {
    this.newQuantity = parseInt(value, 10) || 1
  }

  static fromInventoryScopeChangeItem(item: ScopeChangeRequestItem) {
    return new EditScopeChangeInventoryItemViewModel(
      item.inventoryItemVersionId,
      item.name,
      item.quantity,
      item.quantity,
    )
  }
}
